import type { TFunction } from 'i18next';

import { FILE_POLICY, type FilePurpose, type UploadProblem } from '@/shared/api';

/** The localized message for a file the policy refuses (shown next to the picker, before any upload starts). */
export function uploadProblemMessage(t: TFunction, problem: UploadProblem, fileName: string): string {
  if (problem.code === 'type') return t('files.problem.type', { ns: 'common', types: problem.types });
  if (problem.code === 'too_large') return t('files.problem.tooLarge', { ns: 'common', name: fileName, limit: problem.limitMb });
  return t('files.problem.empty', { ns: 'common', name: fileName });
}

/** "PDF, JPG, PNG · up to 15 MB" per rule of a purpose ("…; XLSX, CSV · up to 10 MB" for attachments). */
export function fileHint(t: TFunction, purpose: FilePurpose): string {
  const parts = FILE_POLICY[purpose].rules.map((r) =>
    t('files.hint', { ns: 'common', types: [...new Set(r.extensions.filter((e) => e !== '.jpeg').map((e) => e.slice(1).toUpperCase()))].join(', '), limit: r.maxBytes / (1024 * 1024) }),
  );
  return parts.reduce((all, part) => t('files.hintJoin', { ns: 'common', first: all, second: part }));
}

/** File size for people: KB below 1 MB, MB with one decimal above (Western digits). */
export function fileSize(t: TFunction, bytes: number): string {
  if (bytes < 1024 * 1024) return t('files.sizeKb', { ns: 'common', value: Math.max(1, Math.round(bytes / 1024)) });
  const tenths = Math.round((bytes * 10) / (1024 * 1024));
  return t('files.sizeMb', { ns: 'common', value: `${Math.floor(tenths / 10)}.${tenths % 10}` });
}
